import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { BaseThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { FallbackIfUnknown } from '@reduxjs/toolkit/dist/tsHelpers';
import { AxiosResponse } from 'axios';
import { genericApiError, isAxiosResponseOk } from '../api/api-common';
import apiManager from '../api/api-manager';
import { PersonaTipoEnum } from '../api/generated';
import {
  DocumentBeanComplexMetadata,
  DocumentNestedGroup,
  DocumentSearchFilterRequest,
  ResponsePaginatedListDocumentBeanComplexMetadata,
  ResponsePaginatedMapStringDocumentNestedGroup,
} from '../api/generated/papyrus-metadata';
import {
  BundleState,
  ResponsePaginatedListBundleState,
} from '../api/generated/signroom-orchestrator';
import { env } from '../env';
import { WalletSections } from '../model';
import {
  DownloadDocumentsError,
  MyWalletState,
  SignatureDocumentError,
  UserState,
  WalletError,
  WalletPayloadAction,
  WalletState,
} from '../store';
import { AsyncThunkConfig } from '../store/async-thunk-config';
import {
  formatDateFromForServer,
  formatDateToForServer,
} from '../api/parsers/commons-api-parsers';

export type GetState<ThunkApiConfig> = ThunkApiConfig extends {
  state: infer State;
}
  ? State
  : unknown;
export type GetExtra<ThunkApiConfig> = ThunkApiConfig extends {
  extra: infer Extra;
}
  ? Extra
  : unknown;
export type GetDispatch<ThunkApiConfig> = ThunkApiConfig extends {
  dispatch: infer Dispatch;
}
  ? FallbackIfUnknown<
      Dispatch,
      ThunkDispatch<
        GetState<ThunkApiConfig>,
        GetExtra<ThunkApiConfig>,
        AnyAction
      >
    >
  : ThunkDispatch<
      GetState<ThunkApiConfig>,
      GetExtra<ThunkApiConfig>,
      AnyAction
    >;
export type GetThunkAPI<ThunkApiConfig> = BaseThunkAPI<
  GetState<ThunkApiConfig>,
  GetExtra<ThunkApiConfig>,
  GetDispatch<ThunkApiConfig>,
  GetRejectValue<ThunkApiConfig>,
  GetRejectedMeta<ThunkApiConfig>,
  GetFulfilledMeta<ThunkApiConfig>
>;
export type GetRejectValue<ThunkApiConfig> = ThunkApiConfig extends {
  rejectValue: infer RejectValue;
}
  ? RejectValue
  : unknown;
export type GetFulfilledMeta<ThunkApiConfig> = ThunkApiConfig extends {
  fulfilledMeta: infer FulfilledMeta;
}
  ? FulfilledMeta
  : unknown;
export type GetRejectedMeta<ThunkApiConfig> = ThunkApiConfig extends {
  rejectedMeta: infer RejectedMeta;
}
  ? RejectedMeta
  : unknown;

const MAX_PAGES = 30;

export const getAPIRequestHeaders = <T = unknown>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  headers?: any
): any => {
  const token: string = thunkApi.getState().data.user.token;

  return { headers: { Authorization: `Bearer ${token}`, ...headers } };
};

export const getAPIRequestHeadersProspect = <T = unknown>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  headers?: any
): any => {
  const token: string = thunkApi.getState().data.user.prospectToken;

  return { headers: { Authorization: `Bearer ${token}`, ...headers } };
};

export const getSelectedSection = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
): WalletSections => {
  const walletState: WalletState = thunkApi.getState().features.walletStore;

  return walletState.common.selectedSection;
};

export const getCurrentState = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
): MyWalletState => {
  const walletState: WalletState = thunkApi.getState().features.walletStore;

  return getSelectedSection(thunkApi) === 'MY_WALLET'
    ? walletState.myWallet
    : walletState.sharedAccounts;
};

export const createWalletPayloadAction = <T>(
  payload: T,
  thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
): WalletPayloadAction<T> => ({
  selectedSection: getSelectedSection(thunkApi),
  payload,
});

export const getPendingMeta = (_, { getState }) => {
  const walletState: WalletState = getState().features.walletStore;
  return {
    selectedSection: walletState.common.selectedSection,
  };
};

Promise.allSettled =
  Promise.allSettled ||
  ((promises) =>
    Promise.all(
      promises.map((p) =>
        p
          .then((value) => ({
            status: 'fulfilled',
            value,
          }))
          .catch((reason) => ({
            status: 'rejected',
            reason,
          }))
      )
    ));

const waitForCustomerCode = <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  timeout = 5000
): Promise<string> => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkCode = () => {
      const state: any = thunkApi.getState();
      const user = state.data?.user;
      const customerCode = user?.userCode;
      const isPG = user?.userType === PersonaTipoEnum.Pg;
      const behalfOfCode = user?.userBehalfOfCode;

      const customerCode2nd = state.features?.userRegistration?.userCode;

      // For PG users, we must wait for both customerCode and userBehalfOfCode to be populated
      const isReady = isPG
        ? customerCode && behalfOfCode
        : customerCode || customerCode2nd;

      if (isReady) {
        resolve(customerCode || customerCode2nd);
      } else if (Date.now() - startTime > timeout) {
        console.warn(
          `[waitForCustomerCode] Timed out after ${timeout}ms. isPG: ${isPG}, code: ${customerCode}, behalf: ${behalfOfCode}`
        );
        resolve(customerCode || customerCode2nd);
      } else {
        setTimeout(checkCode, 100); // Check again in 100ms
      }
    };
    checkCode();
  });
};

const waitForToken = <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  timeout = 5000
): Promise<string> => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkToken = () => {
      const state: any = thunkApi.getState();
      const user = state.data?.user;
      const isFiduciaria = user?.fiduciaria;
      const setContextStatus = user?.setContextStatus;
      const token = isFiduciaria ? user?.tokenRubrica : user?.token;

      // Se non è fiduciaria, il token di login va bene.
      // Se è fiduciaria, dobbiamo aspettare che setContextStatus sia 'succeeded' o 'idle'
      // per essere sicuri di avere il token di contesto.
      if (
        token &&
        (!isFiduciaria ||
          setContextStatus === 'succeeded' ||
          setContextStatus === 'idle')
      ) {
        resolve(token);
      } else if (Date.now() - startTime > timeout) {
        resolve(token);
      } else {
        setTimeout(checkToken, 100);
      }
    };
    checkToken();
  });
};

const searchDocumentsGroupedByBundle = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  filter: DocumentSearchFilterRequest,
  currentPage: number,
  token?: string
): Promise<
  AxiosResponse<ResponsePaginatedMapStringDocumentNestedGroup, any>
> => {
  await waitForCustomerCode(thunkApi);
  if (!token) {
    await waitForToken(thunkApi);
  }

  const state: any = thunkApi.getState();
  const user = state.data.user;
  const isPG = token
    ? state.features.userRegistration.selectedOptionUserType ===
      PersonaTipoEnum.Pg
    : user.userType === PersonaTipoEnum.Pg;
   const isPF = !isPG;

  const customerCode =
    user.userCode || state.features?.userRegistration?.userCode;
  const userBehalfOfCode =
    isPG && !token
      ? user.userBehalfOfCode
      : state.features.userRegistration.userBehalfOfCode;

  const fieldsWithNullValueFilter = filter.fieldsWithNullValueFilter
    ? [...filter.fieldsWithNullValueFilter]
    : [];
   if (isPF && !fieldsWithNullValueFilter.includes('customerUserBehalfOfCode') && !filter.signroom) {
    fieldsWithNullValueFilter.push('customerUserBehalfOfCode');
  }

  const result =
    await apiManager.azDocumentProviderApi.searchDocumentsGroupedByBundle1(
      isPG,
      {
        ...filter,
        customerCode: filter.customerCode ?? customerCode ?? '',
        customerUserBehalfOfCode:
          filter.customerUserBehalfOfCode ?? userBehalfOfCode,
        fieldsWithNullValueFilter,
        paginator: {
          current: currentPage,
        },
      },
      getAPIRequestHeadersWithToken(thunkApi, token)
    );

  if (!isAxiosResponseOk(result)) {
    return genericApiError();
  } else {
    return result;
  }
};

const searchDocumentsBundle = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  currentPage: number,
  token?: string,
  behalf?: boolean
): Promise<AxiosResponse<ResponsePaginatedListBundleState, any>> => {
  const result = await apiManager.signroomProviderApi.searchBundles1(
    behalf,
    {
      recipient: thunkApi.getState().data?.user?.userCode,
      paginator: {
        current: currentPage,
      },
      bundleStatus: ['CREATED', 'SIGNING'],
      recipientStatus: ['NOTIFIED_TO_SIGN'],
    },
    getAPIRequestHeadersWithToken(thunkApi, token)
  );

  if (!isAxiosResponseOk(result)) {
    return genericApiError();
  } else {
    return result;
  }
};

export const searchDocuments = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  filter: DocumentSearchFilterRequest,
  token?: string,
  _signroomFiduciaria?: boolean
): Promise<{ [key: string]: DocumentNestedGroup }> => {
  let result: { [key: string]: DocumentNestedGroup } = {};
  let total = 0;
  let currentPage = 0;

  do {
    const response = await searchDocumentsGroupedByBundle(
      thunkApi,
      filter,
      currentPage,
      token
    );
    result = { ...result, ...response.data.result };
    total = response.data.paginator.total;
    currentPage++;
  } while (total > Object.keys(result).length && currentPage < MAX_PAGES);

  return result;
};

export const searchBundles = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  token?: string,
  behalf?: boolean
): Promise<BundleState[]> => {
  const resultMap = new Map<string, BundleState>();
  let total = 0;
  let currentPage = 0;

  do {
    const response = await searchDocumentsBundle(
      thunkApi,
      currentPage,
      token,
      behalf
    );

    response.data.result.forEach((bundle) => {
      if (bundle.bundleId) resultMap.set(bundle.bundleId, bundle);
    });

    total = response.data.paginator.total;
    currentPage++;
  } while (total > resultMap.size && currentPage < MAX_PAGES);

  return Array.from(resultMap.values());
};

const searchDocumentsByFilter1 = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  filter: Omit<DocumentSearchFilterRequest, 'paginator'>,
  currentPage: number,
  token?: string,
  pageSize?: number
): Promise<
  AxiosResponse<ResponsePaginatedListDocumentBeanComplexMetadata, any>
> => {
  await waitForCustomerCode(thunkApi);
  if (!token) {
    await waitForToken(thunkApi);
  }

  const state: any = thunkApi.getState();
  const user = state.data.user;
  const userType = user.userType;
  const isPG = userType === PersonaTipoEnum.Pg;
  const customerCode = user.userCode;
  const userBehalfOfCode = user.userBehalfOfCode;

  const response =
    await apiManager.azDocumentProviderApi.searchDocumentsByFilter1(
      isPG,
      {
        ...filter,
        customerCode: filter.customerCode ?? customerCode,
        customerUserBehalfOfCode:
          filter.customerUserBehalfOfCode ?? userBehalfOfCode,
        paginator: {
          current: currentPage,
          pageSize: pageSize,
        },
      },
      getAPIRequestHeadersWithToken(thunkApi, token)
    );

  if (!isAxiosResponseOk(response)) {
    return genericApiError();
  } else {
    return response;
  }
};

export const searchDocumentsByFilter = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  filter: DocumentSearchFilterRequest,
  token?: string
): Promise<Array<DocumentBeanComplexMetadata>> => {
  const resultMap = new Map<string, DocumentBeanComplexMetadata>();
  let total = 0;
  let currentPage = 0;

  do {
    const response = await searchDocumentsByFilter1(
      thunkApi,
      filter,
      currentPage,
      token
    );

    response.data.result.forEach((doc) => {
      if (doc.id) resultMap.set(doc.id, doc);
    });

    total = response.data.paginator.total;
    currentPage++;
  } while (total > resultMap.size && currentPage < MAX_PAGES);

  return Array.from(resultMap.values());
};

export const countDocumentsByFilter = async <T>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  filter: DocumentSearchFilterRequest,
  token?: string
): Promise<number> => {
  const response = await searchDocumentsByFilter1(
    thunkApi,
    filter,
    0,
    token,
    1
  );

  return response.data.paginator?.total ?? 0;
};

export const getCleanBundleId = (bundleId: string): string => {
  return bundleId?.split('@')[0] || bundleId;
};

export const shouldSkipBundleApiCall = (bundleId: string): boolean => {
  return bundleId?.includes('mig-feq') ?? false;
};

export const getBundlesDetails = async (
  bundles: DocumentNestedGroup[],
  thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>,
  token?: string
): Promise<
  { documents: DocumentBeanComplexMetadata[]; details: BundleState }[]
> => {
  const details: {
    documents: DocumentBeanComplexMetadata[];
    details: BundleState;
  }[] = [];
  for (const bundle of bundles) {
    const bundleId = bundle[0].bundleId.split('@')[0];

    let bundleDetails: BundleState;

    // Skip API call if bundleId contains "mig-feq", use default empty state
    if (shouldSkipBundleApiCall(bundleId)) {
      bundleDetails = {
        bundleId: bundleId,
        ...bundle[1],
      } as BundleState;
    } else {
      const result = await apiManager.signroomProviderApi.getBundleState1(
        bundleId,
        getAPIRequestHeadersWithToken(thunkApi, token)
      );
      bundleDetails = result.data.result;
    }

    details.push({
      documents: bundle as unknown as DocumentBeanComplexMetadata[],
      details: bundleDetails,
    });
  }
  return details;
};

export const getAPIRequestHeadersWithToken = <T = unknown>(
  thunkApi: GetThunkAPI<AsyncThunkConfig<T>>,
  token?: string,
  headers?: any
) =>
  token
    ? { headers: { Authorization: `Bearer ${token}`, ...headers } }
    : getAPIRequestHeaders(thunkApi, headers);

/*
  adds filter fields for contracts, dispositions and expired documents
*/
export const addFilter = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>,
  filter: DocumentSearchFilterRequest
): Omit<DocumentSearchFilterRequest, 'paginator'> => {
  const usrState: UserState = thunkApi.getState().data.user;
  const isPg = usrState.userType === PersonaTipoEnum.Pg;
  const isPf = usrState.userType === PersonaTipoEnum.Pf;
  filter.customerCode = usrState.userCode;
  if (isPg) {
    filter.customerUserBehalfOfCode = usrState.userBehalfOfCode;
  }
  if (usrState.fiduciaria && usrState.codiceRubrica) {
    filter.customerUserRegisterCode = usrState.codiceRubrica;
  }
  if (filter.fieldsWithNullValueFilter === undefined) {
    filter.fieldsWithNullValueFilter = [];
  }
  if (isPf) {
    if (!filter.fieldsWithNullValueFilter.includes('customerUserBehalfOfCode')) {
      filter.fieldsWithNullValueFilter.push('customerUserBehalfOfCode');
    }
  }

  return filter;
};
export const addOfferFilter = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
): Omit<DocumentSearchFilterRequest, 'paginator'> => {
  const filter: DocumentSearchFilterRequest = {};
  const isPg = thunkApi.getState().data.user.userType === PersonaTipoEnum.Pg;
  //const isPf = thunkApi.getState().data.user.userType === PersonaTipoEnum.Pf;
  const usrState: UserState = thunkApi.getState().data.user;
  filter.scopes = ['INFORMATIONOFFER'];
  filter.customerCode = usrState.userCode;

  if (usrState.fiduciaria && usrState.codiceRubrica) {
    filter.customerUserRegisterCode = usrState.codiceRubrica;
  }

  const dateTo = new Date();
  const dateFrom = new Date();
  dateFrom.setFullYear(dateFrom.getFullYear() - 1);

  filter.publicationDateFrom = formatDateFromForServer(dateFrom);
  filter.publicationDateTo = formatDateToForServer(dateTo);

  if (isPg) {
    filter.customerUserBehalfOfCode = usrState.userBehalfOfCode;
  }

  if (filter.fieldsWithNullValueFilter === undefined) {
    filter.fieldsWithNullValueFilter = [];
  }
  /*if (isPf) {
    if (!filter.fieldsWithNullValueFilter.includes('customerUserBehalfOfCode')) {
      filter.fieldsWithNullValueFilter.push('customerUserBehalfOfCode');
    }
  }*/

  return filter;
};

export const getKidUrl = (isin: string): string =>
  `${env?.kidBaseUrl}/${isin}.pdf`;
